import React, { useEffect, useMemo, useState } from 'react';
import { API_CONFIG } from '../config/api';

const API = API_CONFIG.ENDPOINTS.AUDIT_LOGS;

function prettyJSON(value) {
  try { return JSON.stringify(typeof value === 'string' ? JSON.parse(value) : value, null, 2); } catch { return String(value || ''); }
}

const AuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState({ action: '', entity: '' });

  const token = localStorage.getItem('token');

  const fetchLogs = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filters.action) params.set('action', filters.action);
      if (filters.entity) params.set('entity', filters.entity);
      const res = await fetch(`${API}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch audit logs');
      setLogs(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, [filters.action, filters.entity]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return logs;
    return logs.filter(l => {
      const hay = [
        l.id,
        l.action,
        l.entity,
        l.entity_id,
        l.user?.username,
        l.user_role,
        l.created_at,
        l.before_data,
        l.after_data,
        l.meta
      ].map(x => (x == null ? '' : String(x))).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [logs, query]);

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
      <div className="px-4 py-3 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Audit logovi</h2>
          <p className="text-sm text-gray-600">Pregled svih promjena u sistemu (admin).</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Pretraga"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full sm:w-64 px-3 py-3 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
          <select value={filters.entity} onChange={(e) => setFilters(f => ({ ...f, entity: e.target.value }))} className="px-3 py-3 bg-white border border-gray-300 rounded-md text-sm">
            <option value="">Sve entitete</option>
            <option value="User">User</option>
            <option value="Ustanove">Ustanove</option>
            <option value="Aparati">Aparati</option>
            <option value="Dijelovi">Dijelovi</option>
            <option value="NalogZaRad">NalogZaRad</option>
            <option value="UtroseniDijelovi">UtroseniDijelovi</option>
          </select>
          <select value={filters.action} onChange={(e) => setFilters(f => ({ ...f, action: e.target.value }))} className="px-3 py-3 bg-white border border-gray-300 rounded-md text-sm">
            <option value="">Sve akcije</option>
            <option value="CREATE">CREATE</option>
            <option value="UPDATE">UPDATE</option>
            <option value="UPDATE_FULL">UPDATE_FULL</option>
            <option value="UPDATE_CHECKMARK">UPDATE_CHECKMARK</option>
            <option value="DELETE">DELETE</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[40vh] text-gray-600">Učitavanje...</div>
      ) : error ? (
        <div className="p-3 m-4 text-sm text-red-700 bg-red-100 rounded-lg">Greška: {error}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vrijeme</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Korisnik</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Akcija</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entitet</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entity ID</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prije</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Poslije</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filtered.map(log => (
                <tr key={log.id} className="align-top">
                  <td className="px-4 py-2">{log.id}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</td>
                  <td className="px-4 py-2">{log.User?.username || '-'} <span className="text-gray-500 text-xs">({log.user_role || '-'})</span></td>
                  <td className="px-4 py-2">{log.action}</td>
                  <td className="px-4 py-2">{log.entity}</td>
                  <td className="px-4 py-2">{log.entity_id}</td>
                  <td className="px-4 py-2">
                    <pre className="max-w-[420px] whitespace-pre-wrap break-words text-xs bg-gray-50 p-2 rounded border border-gray-200">{prettyJSON(log.before_data)}</pre>
                  </td>
                  <td className="px-4 py-2">
                    <pre className="max-w-[420px] whitespace-pre-wrap break-words text-xs bg-gray-50 p-2 rounded border border-gray-200">{prettyJSON(log.after_data)}</pre>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AuditLogsPage;
